import { openedSessionTabAtom, openedSessionTabEnum } from '@/atoms/sideNavAtoms';
import { useIsScheduleUploadPdfEnabled } from '@/features/flags';
import {
  trackScheduleUploadPromoClicked,
  trackScheduleUploadPromoViewed,
} from '@/models/analytics/features/trackSessionScheduleAnalytics';
import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyFootNote,
  TypographyFootNoteBold,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom } from 'jotai';
import { type ReactNode } from 'react';

interface UploadDiscoveryTooltipProps {
  children: ReactNode;
}

export const UploadDiscoveryTooltip = ({ children }: UploadDiscoveryTooltipProps) => {
  const { t } = useTranslations();
  const [openedSessionTab, setOpenedSessionTab] = useAtom(openedSessionTabAtom);
  const isPdfEnabled = useIsScheduleUploadPdfEnabled();

  const canShow = openedSessionTab !== openedSessionTabEnum.UPCOMING;

  const handleTryItOut = () => {
    trackScheduleUploadPromoClicked({
      display: 'tooltip',
    });
    setOpenedSessionTab(openedSessionTabEnum.UPCOMING);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      trackScheduleUploadPromoViewed({
        display: 'tooltip',
      });
    }
  };

  if (!canShow) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider>
      <Tooltip onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          alignOffset={0}
          sideOffset={8}
          className="max-w-[237px] gap-1 rounded-lg bg-background-primary py-2 pl-3 pr-2"
        >
          <TypographyCaption className="pb-1 text-text-primary-invert">
            {t('sessions.upcomingSessions.sidebarUploadTitle')}
          </TypographyCaption>
          <TypographyFootNote className="text-text-primary-invert/80">
            {t(
              isPdfEnabled
                ? 'sessions.upcomingSessions.sidebarUploadDescriptionWithPdf'
                : 'sessions.upcomingSessions.sidebarUploadDescription'
            )}
          </TypographyFootNote>
          <Button
            onClick={handleTryItOut}
            variant="link"
            size="xs"
            className="px-0"
            aria-label={t('demo.tryItOut')}
          >
            <TypographyFootNoteBold className="text-text-primary-invert">
              {t('demo.tryItOut')}
            </TypographyFootNoteBold>
          </Button>
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
